import ListingCard from "@/components/ListingCard";

/**
 * Main results area of the Browse page — handles the three UI states:
 *   1. loading                → "Loading listings…"
 *   2. no listings            → empty-state bulldog artwork
 *   3. results (grouped/flat) → grouped-by-sport (default) or flat grid (sport filter)
 *
 * Also renders the "Load more" pagination controls when a sport filter is active.
 */
export default function BrowseListingsGrid({
  loading,
  listings,
  filters,
  setFilters,
  likedIds,
  hasMore,
  loadMore,
  loadingMore,
  totalListings,
  pageSize,
}) {
  if (loading) {
    return (
      <div className="py-24 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">
        Loading listings…
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className="py-16 text-center flex flex-col items-center">
        <img src="/graphics/empty-browse.png" alt="Bulldog waiting" className="max-w-[280px] w-full opacity-80" />
        <div className="font-display font-black uppercase text-2xl text-zinc-400 mt-6">No listings yet.</div>
        <p className="mt-2 text-sm text-zinc-500">Be the first to drop a card in the hunt.</p>
      </div>
    );
  }

  // Grouped-by-sport view (no sport filter). Each section caps at 25 newest.
  if (!filters.sport) {
    const PER_SPORT_CAP = 25;
    const groups = {};
    for (const l of listings) {
      const s = l.sport || "Other";
      (groups[s] = groups[s] || []).push(l);
    }
    const sortedSports = Object.keys(groups).sort((a, b) => groups[b].length - groups[a].length);
    return (
      <div className="space-y-10" data-testid="browse-groups">
        {sortedSports.map((sport) => {
          const total = groups[sport].length;
          const shown = groups[sport].slice(0, PER_SPORT_CAP);
          const hasMoreInGroup = total > shown.length;
          return (
            <section key={sport} data-testid={`browse-group-${sport}`}>
              <div className="flex items-baseline justify-between mb-3 pb-2 border-b border-white/10">
                <div className="flex items-baseline gap-3">
                  <h2 className="font-display font-black uppercase text-lg md:text-xl tracking-tight">{sport}</h2>
                  <span className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase" data-testid={`browse-group-count-${sport}`}>
                    {hasMoreInGroup ? `showing 25 of ${total}` : `${total} listing${total === 1 ? "" : "s"}`}
                  </span>
                </div>
                <button
                  onClick={() => setFilters({ ...filters, sport })}
                  data-testid={`browse-group-viewall-${sport}`}
                  className="font-mono text-[10px] text-[#FF5F1F] tracking-widest uppercase hover:underline"
                >
                  View all{hasMoreInGroup ? ` ${total} ` : " "}→
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
                {shown.map((l) => <ListingCard key={l.id} listing={l} liked={likedIds.has(l.id)} />)}
              </div>
            </section>
          );
        })}
      </div>
    );
  }

  // Flat grid — sport filter is active. Supports paginated "Load more".
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4" data-testid="browse-flat">
        {listings.map((l) => <ListingCard key={l.id} listing={l} liked={likedIds.has(l.id)} />)}
      </div>
      {hasMore && (
        <div className="mt-8 flex flex-col items-center gap-2" data-testid="browse-load-more-wrap">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            Showing {listings.length} of {totalListings}
          </div>
          <button
            onClick={loadMore}
            disabled={loadingMore}
            data-testid="browse-load-more"
            className="h57-btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
          >
            {loadingMore ? "Loading…" : `Load ${Math.min(pageSize, totalListings - listings.length)} More →`}
          </button>
        </div>
      )}
      {!hasMore && listings.length > pageSize && (
        <div className="mt-8 text-center font-mono text-[10px] uppercase tracking-widest text-zinc-500" data-testid="browse-end-of-results">
          You&apos;ve reached the end · {totalListings} listing{totalListings === 1 ? "" : "s"}
        </div>
      )}
    </>
  );
}
